import React, { memo, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import GameCard from '../GameCard/GameCard';
import { preloadImages } from '../LazyImage/LazyImage';

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 12px;
  
  @media (max-width: 767px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const SkeletonLoader = styled.div`
  width: 100%;
  height: 195px;
  background: linear-gradient(
    90deg,
    rgba(190, 190, 190, 0.2) 25%,
    rgba(129, 129, 129, 0.24) 37%,
    rgba(190, 190, 190, 0.2) 63%
  );
  background-size: 400% 100%;
  animation: skeleton-loading 1.4s ease infinite;
  border-radius: 8px;

  @keyframes skeleton-loading {
    0% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }

  @media (max-width: 768px) {
    height: 160px;
  }
`;

const GameGrid = memo(({ 
  games = [], 
  onGameClick, 
  loading = false,
  onImageLoad,
  onImageError
}) => {
  // Preload images for better performance
  useEffect(() => {
    if (games && games.length > 0) {
      const imageUrls = games.slice(0, 20).map(game => game.game_icon); // Preload first 20
      preloadImages(imageUrls, 5); // Load 5 at a time
    }
  }, [games]);

  const handleImageLoad = useCallback((gameId) => {
    onImageLoad?.(gameId);
  }, [onImageLoad]);

  const handleImageError = useCallback((gameId) => {
    onImageError?.(gameId);
  }, [onImageError]);

  if (loading) {
    return (
      <GridWrapper>
        {Array.from({ length: 8 }).map((_, index) => (
          <SkeletonLoader key={index} />
        ))}
      </GridWrapper>
    );
  }

  return (
    <GridWrapper>
      {games.map((game, index) => (
        <GameCard
          key={game.id || index}
          game={game}
          index={index}
          onGameClick={onGameClick}
          onImageLoad={handleImageLoad}
          onImageError={handleImageError}
        />
      ))}
    </GridWrapper>
  );
});

GameGrid.displayName = 'GameGrid';

export default GameGrid;